import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import { Visibility, Add } from '@mui/icons-material';
import { StocktakeService } from '../../services/stocktake.service';
import {
    StocktakeDTO,
    StocktakeStatus,
    STOCKTAKE_STATUS_LABELS,
} from '../../interfaces/stocktake.interface';

const PAGE_SIZE = 10;

// Posted is done, pending approval needs someone, draft and cancelled are quiet.
const STATUS_COLORS: Record<StocktakeStatus, 'default' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default',
    PENDING_APPROVAL: 'warning',
    POSTED: 'success',
    CANCELLED: 'error',
};

const STATUS_OPTIONS: StocktakeStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'POSTED', 'CANCELLED'];

const StocktakeList: React.FC = () => {
    const navigate = useNavigate();
    const [stocktakes, setStocktakes] = useState<StocktakeDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0,
    });

    const handleViewDetails = (id: string) => {
        navigate(`/stocktakes/${id}`);
    };

    const fetchStocktakes = async (page: number = 0, status: string = statusFilter) => {
        try {
            setLoading(true);
            const response = await StocktakeService.getStocktakesByInstitution(status, page);
            setStocktakes(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
            setError('');
        } catch (err) {
            setError('Failed to load stocktakes');
            console.error('Error fetching stocktakes:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStocktakes(0, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value);
    };

    const handlePageChange = (_event: unknown, page: number) => {
        fetchStocktakes(page, statusFilter);
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="store.counts.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Stocktakes</Typography>
                <Button
                    data-help="store.counts.new"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/stocktakes/create')}
                >
                    New Stocktake
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.counts.filter">
                <FormControl size="small" sx={{ flex: '1 1 220px', maxWidth: 260 }}>
                    <InputLabel>Status</InputLabel>
                    <Select data-help="store.counts.filter.status" value={statusFilter} label="Status" onChange={handleStatusChange}>
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>
                                {STOCKTAKE_STATUS_LABELS[status]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    Total: {pageInfo.totalElements}
                </Typography>
            </Box>

            {loading ? (
                <div data-testid="loading-state">Loading...</div>
            ) : (
                <TableContainer component={Paper} data-help-section="store.counts.table">
                    <Table size="small" data-testid="stocktakes-table" sx={{ minWidth: 640 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                                <TableCell data-help="store.counts.col.refNo">Reference No</TableCell>
                                <TableCell data-help="store.counts.col.store">Store</TableCell>
                                <TableCell data-help="store.counts.col.date">Date</TableCell>
                                <TableCell data-help="store.counts.col.status">Status</TableCell>
                                <TableCell data-help="store.counts.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stocktakes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No stocktakes found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stocktakes.map((stocktake) => (
                                    <TableRow key={stocktake.id} hover data-testid={`stocktake-row-${stocktake.id}`}>
                                        <TableCell>{stocktake.referenceNo}</TableCell>
                                        <TableCell>{stocktake.storeName || '-'}</TableCell>
                                        <TableCell>
                                            {stocktake.stocktakeDate ? formatDate(stocktake.stocktakeDate) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={STATUS_COLORS[stocktake.status] ?? 'default'}
                                                label={STOCKTAKE_STATUS_LABELS[stocktake.status] ?? stocktake.status}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(stocktake.id)}
                                                    color="primary"
                                                    data-testid={`view-button-${stocktake.id}`}
                                                    data-help="store.counts.action.view"
                                                    aria-label="View Details"
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={pageInfo.totalElements}
                        page={pageInfo.currentPage}
                        onPageChange={handlePageChange}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </Box>
    );
};

export default StocktakeList;
